import { STANDARD_QUESTIONNAIRES, isValidQuestionnaireScoring, getObservationDisplayByLoincCode, WSAS_WORK_NOT_APPLICABLE_VALUE, QUESTIONNAIRE_URLS, getQuestionnaireDefinition, PHQ9_SCORING, PHQ9A_SCORING, GAD7_SCORING, NPS_SCORING } from './constants.mjs';

/** Gets questionnaire configuration by URL. @agentic @benchmark/core */ const getQuestionnaireConfig = (url)=>Object.values(STANDARD_QUESTIONNAIRES).find((config)=>config.url === url);

/**
 * Reverse mapping from LOINC code to questionnaire URL.
 * @agentic @benchmark/core
 */ const getQuestionnaireUrlFromLoincCode = (loincCode)=>{
    for (const config of Object.values(STANDARD_QUESTIONNAIRES)){
        if (config.totalScoreLoincCode === loincCode) {
            return config.url;
        }
    }
    return null;
};
/**
 * Returns LOINC code and display name for questionnaire total score.
 * @agentic @benchmark/core
 */ const getLoincCodeForQuestionnaire = (url)=>{
    const config = getQuestionnaireConfig(url);
    if (!config) return null;
    // Use standardized display name from constants (short names like "PHQ-4", "GAD-7")
    const displayName = getObservationDisplayByLoincCode(config.totalScoreLoincCode);
    return {
        code: config.totalScoreLoincCode,
        display: displayName
    };
};
/**
 * Maps risk level to FHIR ObservationInterpretation code.
 * @agentic @benchmark/core
 */ const mapRiskLevelToFhirCode = (level)=>{
    const levelValue = typeof level === "object" ? level.value : level;
    switch(levelValue){
        case "minimal":
            return "N"; // Normal
        case "low":
            return "L"; // Low
        case "mild":
            return "L"; // Low
        case "moderate":
            return "H"; // High
        case "high":
            return "H"; // High
        case "moderately-severe":
            return "HH"; // Critical high
        case "severe":
            return "HH"; // Critical high
        // Categorical screening tiers (PSC-17, PHQ-4, PC-PTSD-5, TAPS, SBIRT, ...).
        // A positive screen is "outside normal" and MUST map to Abnormal — never fall
        // through to the "N" (Normal) default, which would make downstream triage keyed
        // on the interpretation code silently treat a positive screen as normal.
        // (`subclinical` is deliberately NOT mapped here: its only production use is the
        // NPS "Passive" tier, a satisfaction metric that is not a clinical abnormality.)
        case "negative":
            return "N"; // Normal
        case "positive":
            return "A"; // Abnormal
        default:
            return "N"; // Default to normal
    }
};
/**
 * Returns FHIR interpretation coding from score and scoring config.
 * @agentic @benchmark/core
 */ const getInterpretationFromScore = (score, scoring)=>{
    if (!scoring.ranges) return undefined;
    const range = scoring.ranges.find((r)=>score >= r.min && score <= r.max);
    if (!range) return undefined;
    return [
        {
            coding: [
                {
                    system: "http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation",
                    code: mapRiskLevelToFhirCode(range.level),
                    display: range.interpretation
                }
            ]
        }
    ];
};
/**
 * Builds a FHIR interpretation coding array from a score's risk level and text.
 * Returns undefined for unknown risk so we never persist a misleading "Normal".
 * @agentic @benchmark/core
 */ const buildScoreInterpretation = (interpretation, riskLevel)=>{
    if (!interpretation || !riskLevel || riskLevel === "unknown") {
        return undefined;
    }
    return [
        {
            coding: [
                {
                    system: "http://terminology.hl7.org/CodeSystem/v3-ObservationInterpretation",
                    code: mapRiskLevelToFhirCode(riskLevel),
                    display: interpretation
                }
            ]
        }
    ];
};
// =============================================================================
// SPECIALIZED SCORING METHODS
// =============================================================================
/**
 * Calculates WPAI score based on percentage-based impairment (not simple summation).
 * @agentic @benchmark/core
 */ const calculateWPAIScore = (responses)=>{
    // Get individual response values
    const getResponse = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value ?? 0;
    const employed = getResponse("wpai-1"); // 0 = No, 1 = Yes
    const hoursHealthMissed = getResponse("wpai-2"); // 0-6 scale (0=0hrs, 6=40+ hrs)
    const hoursWorked = getResponse("wpai-4"); // 0-5 scale (0=0hrs, 5=40+ hrs)
    const workProductivity = getResponse("wpai-5"); // 0-10 scale
    const activityImpairment = getResponse("wpai-6"); // 0-10 scale
    // Convert scales to approximate hours for calculation
    const healthMissedHours = [
        0,
        4.5,
        12.5,
        20.5,
        28.5,
        36.5,
        45
    ][hoursHealthMissed] || 0;
    const actualWorkedHours = [
        0,
        5.5,
        15.5,
        25.5,
        35.5,
        45
    ][hoursWorked] || 0;
    let maxImpairment = 0;
    // Calculate Activity Impairment (always calculated)
    const activityImpairmentPercent = activityImpairment / 10 * 100;
    maxImpairment = Math.max(maxImpairment, activityImpairmentPercent);
    // Calculate work-related metrics only if employed
    if (employed === 1) {
        // Calculate Absenteeism (percentage of work time missed due to health)
        let absenteeismPercent = 0;
        if (healthMissedHours > 0 && healthMissedHours + actualWorkedHours > 0) {
            absenteeismPercent = healthMissedHours / (healthMissedHours + actualWorkedHours) * 100;
        }
        // Calculate Presenteeism (impairment while working) only if actually worked
        const presenteeismPercent = actualWorkedHours > 0 ? workProductivity / 10 * 100 : 0;
        // Calculate Overall Work Productivity Loss
        const workProductivityLossPercent = absenteeismPercent + presenteeismPercent * (1 - absenteeismPercent / 100);
        // Use the highest impairment percentage as the overall score
        if (presenteeismPercent > maxImpairment) {
            maxImpairment = presenteeismPercent;
        }
        if (workProductivityLossPercent > maxImpairment) {
            maxImpairment = workProductivityLossPercent;
        }
    }
    // Find the appropriate range for the maximum impairment percentage. Absenteeism
    // and overall work-productivity loss are fractional (derived from the WPAI hour
    // approximations), so match the range against the same rounded score we report:
    // matching the raw fraction against integer band boundaries leaves gaps
    // (e.g. 50.5 falls between the 26–50 and 51–75 bands and would misreport as
    // "Unable to determine" for a valid response).
    const roundedImpairment = Math.round(maxImpairment);
    const ranges = [
        {
            min: 0,
            max: 25,
            level: "minimal",
            interpretation: "Minimal impairment"
        },
        {
            min: 26,
            max: 50,
            level: "mild",
            interpretation: "Mild impairment"
        },
        {
            min: 51,
            max: 75,
            level: "moderate",
            interpretation: "Moderate impairment"
        },
        {
            min: 76,
            max: 100,
            level: "severe",
            interpretation: "Severe impairment"
        }
    ];
    const range = ranges.find((r)=>roundedImpairment >= r.min && roundedImpairment <= r.max);
    return {
        score: roundedImpairment,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level || "unknown",
        riskColor: range?.level === "severe" ? "red" : range?.level === "moderate" ? "orange" : range?.level === "mild" ? "yellow" : "gray"
    };
};
/**
 * Calculates C-SSRS score from ordinal severity levels (highest level reached).
 * @agentic @benchmark/core
 */ const calculateCSSRSScore = (responses, scoring)=>{
    // C-SSRS certified branching: Q1 (wish to be dead) and Q2 (active thoughts)
    // are always asked. Q3-Q5 are asked ONLY if Q2 is Yes. `gateFor` hides Q3-Q5
    // in the UI when Q2 is No, but stale answers can still survive on the saved
    // response, so scoring must enforce the gate itself — never derive a level
    // from Q3-Q5 unless Q2 is positive.
    const answerFor = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value || 0;
    let highestLevel = 0;
    if (answerFor("c-ssrs-ideation-1") > 0) {
        highestLevel = 1;
    }
    const activeIdeation = answerFor("c-ssrs-ideation-2") > 0;
    if (activeIdeation) {
        highestLevel = 2;
        // Q3-Q5 only count when Q2 (active ideation) is positive.
        if (answerFor("c-ssrs-ideation-3") > 0) {
            highestLevel = 3;
        }
        if (answerFor("c-ssrs-ideation-4") > 0) {
            highestLevel = 4;
        }
        if (answerFor("c-ssrs-ideation-5") > 0) {
            highestLevel = 5;
        }
    }
    // Suicidal behavior raises risk regardless of ideation level, per the
    // certified Columbia triage: lifetime behavior floors at Moderate (level 3),
    // behavior within the past 3 months floors at High (level 4).
    const hasLifetimeBehavior = answerFor("c-ssrs-behavior-1") > 0;
    if (hasLifetimeBehavior && highestLevel < 3) {
        highestLevel = 3;
    }
    // Recency is only asked after lifetime behavior is Yes (certified branching).
    // A recency answer without positive lifetime behavior is stale/contradictory
    // data and must NOT raise risk.
    if (hasLifetimeBehavior && answerFor("c-ssrs-behavior-recency") > 0 && highestLevel < 4) {
        highestLevel = 4;
    }
    const severityLevels = scoring.severityLevels;
    const severityLevel = severityLevels?.find((level)=>level.level === highestLevel);
    const riskLevelValue = severityLevel?.riskLevel?.value || (highestLevel === 0 ? "minimal" : "unknown");
    return {
        score: highestLevel,
        interpretation: severityLevel?.interpretation || (highestLevel === 0 ? "No suicidal ideation" : "Unable to determine"),
        riskLevel: riskLevelValue,
        // Tier-appropriate color (D5): never signal "green/normal" for active
        // suicidal ideation. Overrides the shared risk-level palette where its
        // color (e.g. "low" → green) would under-signal C-SSRS urgency.
        riskColor: cssrsRiskColor(riskLevelValue)
    };
};
/**
 * Maps a C-SSRS risk-tier value to a display color reflecting triage urgency.
 * @agentic @benchmark/core
 */ const cssrsRiskColor = (riskLevelValue)=>{
    switch(riskLevelValue){
        case "minimal":
            return "green";
        case "low":
            return "yellow";
        case "moderate":
            return "orange";
        case "high":
            return "red";
        default:
            return "gray";
    }
};
/**
 * Calculates WSAS total with work-item N/A rescale (PRD §8.8).
 * @agentic @benchmark/core
 */ const calculateWSASScore = (responses, scoring)=>{
    const WORK_LINK_ID = "wsas-1";
    const workValue = responses.find((r)=>r.linkId === WORK_LINK_ID)?.value;
    const workNotApplicable = workValue === WSAS_WORK_NOT_APPLICABLE_VALUE;
    let sum = 0;
    let scoredCount = 0;
    for (const linkId of scoring.linkIds){
        const value = responses.find((r)=>r.linkId === linkId)?.value;
        if (value === undefined || value < 0) {
            continue;
        }
        if (linkId === WORK_LINK_ID && workNotApplicable) {
            continue;
        }
        sum += value;
        scoredCount += 1;
    }
    let totalScore = sum;
    if (workNotApplicable && scoredCount === 4) {
        totalScore = Math.round(sum * (5 / 4));
    }
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates ASRS score: sum Part A items (0-4 each), cutpoint ≥14.
 * @agentic @benchmark/core
 */ const calculateASRSScore = (responses, scoring)=>{
    const partALinkIds = scoring.partA?.linkIds ?? [];
    let partASum = 0;
    for (const linkId of partALinkIds){
        const response = responses.find((r)=>r.linkId === linkId);
        partASum += response?.value ?? 0;
    }
    const range = scoring.ranges?.find((r)=>partASum >= r.min && partASum <= r.max);
    return {
        score: partASum,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates questionnaire score from responses and scoring config; handles WPAI, C-SSRS, ASRS, summation.
 * @agentic @benchmark/core
 */ const calculateQuestionnaireScore = (responses, scoring)=>{
    // Strongly typed validation using type guard
    if (!isValidQuestionnaireScoring(scoring)) {
        console.error("Invalid scoring configuration: failed validation", {
            scoring
        });
        return {
            score: 0,
            interpretation: "Unable to determine: Invalid scoring configuration.",
            riskLevel: "unknown",
            riskColor: "gray"
        };
    }
    // Handle special scoring methods
    if (scoring.method === "wpai") {
        return calculateWPAIScore(responses);
    }
    if (scoring.method === "c-ssrs-ordinal") {
        return calculateCSSRSScore(responses, scoring);
    }
    if (scoring.method === "asrs-threshold") {
        return calculateASRSScore(responses, scoring);
    }
    if (scoring.method === "wsas-summation") {
        return calculateWSASScore(responses, scoring);
    }
    // Default summation method
    // NOTE: Callers are responsible for filtering out non-scorable responses
    // (items with scorable: false) before calling this function
    const totalScore = responses.reduce((sum, response)=>{
        return sum + (response.value || 0);
    }, 0);
    // Find the appropriate range for the score
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};

/**
 * Sum integer codes for the given link IDs (missing or non-numeric counts as 0).
 * @agentic @benchmark/core
 */ const sumLikertScoresFromAnswerCodes = (answersByLinkId, linkIds)=>{
    let sum = 0;
    for (const id of linkIds){
        const raw = answersByLinkId[id];
        if (raw === undefined || raw === "") {
            continue;
        }
        const n = Number.parseInt(raw, 10);
        if (!Number.isNaN(n)) {
            sum += n;
        }
    }
    return sum;
};
/**
 * Pick the scoring range that contains `total`, or the nearest band if out of range.
 * @agentic @benchmark/core
 */ const getScoringRangeForTotal = (total, ranges)=>{
    if (ranges.length === 0) {
        return undefined;
    }
    const match = ranges.find((r)=>total >= r.min && total <= r.max);
    if (match) {
        return match;
    }
    if (total < ranges[0].min) {
        return ranges[0];
    }
    return ranges[ranges.length - 1];
};
/**
 * Score PHQ-9 or GAD-7 from answer codes using shared questionnaire scoring config.
 * @agentic @benchmark/core
 */ const scoreLikertQuestionnaire = (answersByLinkId, scoring)=>{
    if (!scoring.ranges || scoring.maxScore === undefined) {
        return null;
    }
    const total = sumLikertScoresFromAnswerCodes(answersByLinkId, scoring.linkIds);
    const band = getScoringRangeForTotal(total, scoring.ranges);
    if (!band) {
        return null;
    }
    return {
        total,
        maxScore: scoring.maxScore,
        interpretation: band.interpretation,
        severityDisplay: band.level.display
    };
};
/**
 * Score a registered questionnaire via its registry `scoringMethod`
 * (summation / wpai / asrs-threshold / c-ssrs-ordinal), adapting the result to
 * the shared {@link QuestionnaireLikertScoreResult} shape so the survey API and
 * members results screen consume one contract. Returns null for unknown or
 * unscored (`none`) instruments.
 * @agentic @benchmark/core
 */ const scoreByRegistryMethod = (definition, answersByLinkId)=>{
    // `none` (unscored) and `category` (a labelled result, not a numeric total)
    // produce no score Observation.
    if (definition.scoringMethod === "none" || definition.scoringMethod === "category") {
        return null;
    }
    // Score only the configured (scorable) link IDs, mirroring the Likert branch,
    // so extra/non-scorable answers in the payload (e.g. a screening gate) never
    // inflate the total. Non-numeric/free-text answers don't score.
    const scorableLinkIds = new Set(definition.scoring.linkIds);
    const responses = Object.entries(answersByLinkId).filter(([linkId, code])=>scorableLinkIds.has(linkId) && /^-?\d+$/.test(code)).map(([linkId, code])=>({
            linkId,
            value: Number(code)
        }));
    // The engine dispatches on `scoring.method`; the registry `scoringMethod` is
    // the source of truth, so bridge it in for the special (non-summation) methods
    // in case a `*_SCORING` config omits an explicit `method`.
    const scoring = definition.scoringMethod === "summation" ? definition.scoring : {
        ...definition.scoring,
        method: definition.scoringMethod
    };
    const result = calculateQuestionnaireScore(responses, scoring);
    const band = definition.scoring.ranges ? getScoringRangeForTotal(result.score, definition.scoring.ranges) : undefined;
    return {
        total: result.score,
        maxScore: definition.scoring.maxScore ?? 0,
        interpretation: result.interpretation,
        severityDisplay: band?.level.display ?? result.riskLevel
    };
};
const scoreRegisteredQuestionnaire = (questionnaireUrl, answersByLinkId)=>{
    const definition = getQuestionnaireDefinition(questionnaireUrl);
    // Only instruments approved for the patient survey link are scored on this
    // path. This keeps scoring aligned with the sendable policy: a non-sendable
    // URL (e.g. C-SSRS, PHQ-4) never produces a survey Observation
    // or result card, even if it somehow appears in a token the picker/allow-list
    // would reject. PHQ-9/PHQ-9A/GAD-7 and NPS are handled by the explicit
    // branches above.
    if (!definition || !definition.capabilities.surveyLinkAdministrable) {
        return null;
    }
    return scoreByRegistryMethod(definition, answersByLinkId);
};
/**
 * Scores a submitted survey questionnaire to a total + severity band. PHQ-9,
 * PHQ-9A, GAD-7, and NPS keep the exact Likert summation behavior; every other
 * registered, scorable instrument dispatches on its registry `scoringMethod`.
 * Returns null for unknown or unscored instruments.
 * @agentic @benchmark/core
 */ const scoreByCanonicalQuestionnaireUrl = (questionnaireUrl, answersByLinkId)=>{
    if (questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9) {
        return scoreLikertQuestionnaire(answersByLinkId, PHQ9_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9A) {
        return scoreLikertQuestionnaire(answersByLinkId, PHQ9A_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.GAD7) {
        return scoreLikertQuestionnaire(answersByLinkId, GAD7_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.NPS) {
        return scoreLikertQuestionnaire(answersByLinkId, NPS_SCORING);
    }
    return scoreRegisteredQuestionnaire(questionnaireUrl, answersByLinkId);
};
/**
 * Score a completed instrument administered through **any** channel — including
 * the adaptive screener, which administers scorable instruments that are NOT
 * `surveyLinkAdministrable` (AUDIT-C, OCI-4, PC-PTSD-5, TAPS, ...). Unlike
 * {@link scoreByCanonicalQuestionnaireUrl}, this does not apply the survey-link
 * sendable gate: any registered instrument whose registry `scoringMethod`
 * yields a numeric total is scored, so a remotely-completed screener assessment
 * can emit the score Observation CoCM billing eligibility reads. PHQ-9/PHQ-9A/
 * GAD-7/NPS keep the exact Likert summation behavior. Returns null for unknown,
 * `none`, or `category` (non-numeric) instruments.
 * @agentic @benchmark/core
 */ const scoreAdministeredQuestionnaire = (questionnaireUrl, answersByLinkId)=>{
    if (questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9) {
        return scoreLikertQuestionnaire(answersByLinkId, PHQ9_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9A) {
        return scoreLikertQuestionnaire(answersByLinkId, PHQ9A_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.GAD7) {
        return scoreLikertQuestionnaire(answersByLinkId, GAD7_SCORING);
    }
    if (questionnaireUrl === QUESTIONNAIRE_URLS.NPS) {
        return scoreLikertQuestionnaire(answersByLinkId, NPS_SCORING);
    }
    const definition = getQuestionnaireDefinition(questionnaireUrl);
    if (!definition) {
        return null;
    }
    return scoreByRegistryMethod(definition, answersByLinkId);
};

/**
 * CoCM billing rules — versioned, frozen table.
 *
 * Implements the brief's "Billing Rules Table" verbatim:
 *  - Initial month, assessments present (D14 + D18)
 *  - Subsequent month, assessments present (D13 cascade applies)
 *  - Any month, no eligible assessment (G2214-only)
 *
 * Bounds use the brief's "16 lower bound uniformly" convention (see
 * Boundary precision flag). If the user later confirms 15 for
 * subsequent months, only the two cells in `SUBSEQUENT_WITH_ASSESSMENT`
 * and `NO_ASSESSMENT` change.
 *
 * @agentic @benchmark/core
 */ /** Version stamp included on any copied billing report (D11). @agentic @benchmark/core */ const COCM_BILLING_RULES_VERSION = "COCM_BILLING_RULES_V1";
/**
 * Initial month with eligible assessment (D14).
 * 99492-based ladder.
 * @agentic @benchmark/core
 */ const INITIAL_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 35,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 36,
        maxMinutes: 85,
        codes: [
            "99492"
        ],
        packageDisplay: "99492"
    },
    {
        minMinutes: 86,
        maxMinutes: 115,
        codes: [
            "99492",
            "99494"
        ],
        packageDisplay: "99492 + 99494"
    },
    {
        minMinutes: 116,
        maxMinutes: 145,
        codes: [
            "99492",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×2"
    },
    {
        minMinutes: 146,
        maxMinutes: 175,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×3"
    },
    {
        minMinutes: 176,
        maxMinutes: null,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×4"
    }
];
/**
 * Subsequent month with eligible assessment.
 * 99493-based ladder. Also used as the cascade ladder per D13 when the
 * episode's first bill was G2214.
 * @agentic @benchmark/core
 */ const SUBSEQUENT_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 30,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 31,
        maxMinutes: 75,
        codes: [
            "99493"
        ],
        packageDisplay: "99493"
    },
    {
        minMinutes: 76,
        maxMinutes: 105,
        codes: [
            "99493",
            "99494"
        ],
        packageDisplay: "99493 + 99494"
    },
    {
        minMinutes: 106,
        maxMinutes: 135,
        codes: [
            "99493",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×2"
    },
    {
        minMinutes: 136,
        maxMinutes: 165,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×3"
    },
    {
        minMinutes: 166,
        maxMinutes: null,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×4"
    }
];
/**
 * Any month, no eligible assessment in window (D18).
 * Only G2214 may fire without an assessment.
 * @agentic @benchmark/core
 */ const NO_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: null,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    }
];
/**
 * Care units per billed code — the single TypeScript source of truth, aligned
 * with the dbt `billing_codes` seed (D24): 99492 / 99493 = 1.0 (base month),
 * 99494 = 0.5 (each 30-min add-on), G2214 = 0.25 (GBHI). Summing these over a
 * code package yields the packet's `careUnits`.
 *
 * NOTE (D24): the value for G2214 is 0.25, matching `billing_codes.csv` and both
 * `fct_*_cocm_billing` models. Two documentation strings historically said 0.5;
 * those are the stale ones, not this.
 * @agentic @benchmark/core
 */ const CARE_UNITS_BY_CODE = {
    "99492": 1.0,
    "99493": 1.0,
    "99494": 0.5,
    G2214: 0.25
};
/**
 * Sum the care units for a billed code package. Empty package = 0.
 * @agentic @benchmark/core
 */ const careUnitsForCodes = (codes)=>codes.reduce((sum, code)=>sum + (CARE_UNITS_BY_CODE[code] ?? 0), 0);
/**
 * User-facing legend for the CoCM billing codes shown on the patient billing
 * tab. Thresholds mirror the ladders above (single source of truth): 99492's
 * initial rung starts at 36 (`INITIAL_WITH_ASSESSMENT`), 99493's subsequent
 * rung at 31 (`SUBSEQUENT_WITH_ASSESSMENT`), G2214 at 16 (both ladders); 99494
 * is the per-rung add-on. These are on-read ESTIMATES (D14) — the authoritative
 * code + amount are synthesized in dbt.
 * @agentic @benchmark/core
 */ const COCM_BILLING_CODE_LEGEND = [
    {
        code: "99492",
        label: "Initial",
        minMinutes: 36
    },
    {
        code: "99493",
        label: "Subsequent",
        minMinutes: 31
    },
    {
        code: "99494",
        label: "Add-on",
        minMinutes: null,
        addOnMinutes: 30
    },
    {
        code: "G2214",
        label: "GBHI",
        minMinutes: 16
    }
];

/**
 * Select the billing ladder for the month per D13 + D14 + D18.
 * @agentic @benchmark/core
 */ const selectLadder = (input)=>{
    if (!input.hasEligibleAssessment) return NO_ASSESSMENT;
    // Concern 3: 99492 is billable at most once per episode. Once a prior month
    // billed it, force the subsequent (99493-based) ladder so it can never
    // recur — independent of the initial-month recompute.
    if (input.block99492) return SUBSEQUENT_WITH_ASSESSMENT;
    // D13 cascade: a G2214 initial month locks every later month into the
    // subsequent-month ladder (never 99492).
    if (input.episodeFirstBillWasG2214 && !input.isInitialMonth) {
        return SUBSEQUENT_WITH_ASSESSMENT;
    }
    return input.isInitialMonth ? INITIAL_WITH_ASSESSMENT : SUBSEQUENT_WITH_ASSESSMENT;
};
/**
 * Normalize raw input minutes: clamp negatives to 0, floor fractional
 * values. Every ladder rule + gap calculation operates on the
 * normalized value so the resolver stays internally consistent.
 *
 * Non-finite input (NaN / ±Infinity, e.g. from a corrupted upstream
 * `valueQuantity`) fails CLOSED to 0 → no bill. Without this guard,
 * `NaN` comparisons are all false, so `findRung` skips every bounded
 * rung and lands on the open-ended TOP rung — silently billing the
 * maximal code package for garbage input.
 * @agentic @benchmark/core
 */ const normalizeMinutes = (totalMinutes)=>Number.isFinite(totalMinutes) ? Math.max(0, Math.floor(totalMinutes)) : 0;
/**
 * Locate the rung whose minute window covers `minutes`. The caller is
 * responsible for passing a normalized integer (see `normalizeMinutes`).
 * @agentic @benchmark/core
 */ const findRung = (ladder, minutes)=>ladder.find((rung)=>{
        if (minutes < rung.minMinutes) return false;
        if (rung.maxMinutes === null) return true;
        return minutes <= rung.maxMinutes;
    });
/**
 * Compute the gap (in whole minutes) from `minutes` to the next rung's
 * lower bound. Returns `undefined` when there is no next rung (top of
 * ladder) or when the current rung's `codes` is already the highest
 * non-empty value.
 *
 * Per D15 the widget shows "minutes to next code" — so we always
 * compute relative to the next non-empty rung, even if the current
 * rung itself is non-empty (i.e. "minutes until the next more
 * lucrative package").
 *
 * The caller is responsible for passing a normalized integer; the
 * resolver normalizes once at entry so the gap stays a whole number.
 * @agentic @benchmark/core
 */ const computeMinutesToNext = (ladder, minutes, currentRung)=>{
    if (!currentRung) return undefined;
    const currentIndex = ladder.indexOf(currentRung);
    if (currentIndex === -1) return undefined;
    const nextRung = ladder[currentIndex + 1];
    if (!nextRung) return undefined;
    // Already inside the topmost open-ended rung → no next bound to chase.
    if (nextRung.maxMinutes === null && nextRung.codes.length === 0) {
        return undefined;
    }
    const gap = nextRung.minMinutes - minutes;
    return gap > 0 ? gap : undefined;
};
/**
 * Resolve the billing code package for a CoCM month. Pure function.
 *
 * Contract per the brief:
 *  - Returns empty `codes` when no rule fires (< 16 min, or < 16 min
 *    with no assessment).
 *  - Returns the package (codes + display) per D15.
 *  - Returns `minutesToNextCode` (whole minutes) when there is a
 *    higher-value rung reachable from the current `totalMinutes`.
 *
 * Defensive normalization (negative / fractional minutes are clamped
 * to `Math.max(0, Math.floor(...))`) is applied once at the top so
 * every downstream calculation operates on consistent integer minutes.
 * @agentic @benchmark/core
 */ const resolveBillingCode = (input)=>{
    const ladder = selectLadder(input);
    const minutes = normalizeMinutes(input.totalMinutes);
    const rung = findRung(ladder, minutes);
    if (!rung) {
        // Should never happen — every ladder covers [0, ∞).
        return {
            codes: [],
            packageDisplay: "",
            minutesToNextCode: undefined
        };
    }
    return {
        codes: [
            ...rung.codes
        ],
        packageDisplay: rung.packageDisplay,
        minutesToNextCode: computeMinutesToNext(ladder, minutes, rung)
    };
};

/**
 * v1 assessments-used list. `computeCoCMEligibility` surfaces the freshness
 * signal + the latest eligible assessment date; the fuller per-instrument list
 * is a D26 fast-follow. When an eligible assessment exists we record the latest
 * one as the criterion that satisfied the 99492/99493 requirement.
 */ const assessmentsUsedFromSummary = (summary)=>summary.hasEligibleAssessment && summary.latestAssessmentDate ? [
        {
            code: "cocm-eligible",
            date: summary.latestAssessmentDate
        }
    ] : [];
/**
 * Assemble the versioned billing-code packet for a patient-month. Pure.
 * @agentic @benchmark/core
 */ const buildBillingCodePacket = (input)=>{
    const { patientId, billingMonth, summary, attribution } = input;
    const codes = [
        ...summary.billingResult.codes
    ];
    const enrollmentWindow = summary.cocmPeriodStart || summary.cocmPeriodEnd || summary.cocmCarePlanId ? {
        start: summary.cocmPeriodStart,
        end: summary.cocmPeriodEnd,
        carePlanId: summary.cocmCarePlanId
    } : undefined;
    return {
        packetVersion: 1,
        rulesVersion: COCM_BILLING_RULES_VERSION,
        patientId,
        billingMonth,
        codes,
        packageDisplay: summary.billingResult.packageDisplay,
        careUnits: careUnitsForCodes(codes),
        billableMinutes: summary.billableMinutes,
        totalMinutes: summary.totalMinutes,
        isInitialMonth: summary.isInitialMonth,
        episodeFirstBillWasG2214: summary.episodeFirstBillWasG2214,
        assessmentsUsed: assessmentsUsedFromSummary(summary),
        hasEligibleAssessment: summary.hasEligibleAssessment,
        ...enrollmentWindow ? {
            enrollmentWindow
        } : {},
        ...attribution ? {
            attribution
        } : {},
        generatedAt: input.generatedAt ?? new Date().toISOString()
    };
};

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");
/**
 * Assessment freshness window for CoCM billing eligibility (D18 — 6 weeks).
 * Applied uniformly to every rule that requires an assessment.
 * @agentic @benchmark/core
 */ const COCM_ASSESSMENT_FRESHNESS_WEEKS = 6;

/**
 * Single source of truth for the 6-week eligible-assessment freshness
 * window, expressed in milliseconds. Both the registry FHIR search
 * (`fetchPatientLatestAssessments`) and the client-side trend summary
 * filter (`summarizeAssessmentTrend`) read from here so the two can
 * never drift to different unit conversions.
 * @agentic @benchmark/core
 */ const ASSESSMENT_FRESHNESS_MS = COCM_ASSESSMENT_FRESHNESS_WEEKS * 7 * 24 * 60 * 60 * 1000;
const isActiveStatus = (status)=>status !== "entered-in-error" && status !== "cancelled";
const observationEffective = (observation)=>observation.effectiveDateTime ?? observation.effectivePeriod?.start;
const observationValue = (observation)=>{
    const fromQuantity = observation.valueQuantity?.value;
    if (typeof fromQuantity === "number" && Number.isFinite(fromQuantity)) {
        return fromQuantity;
    }
    const fromInteger = observation.valueInteger;
    if (typeof fromInteger === "number" && Number.isFinite(fromInteger)) {
        return fromInteger;
    }
    return undefined;
};
/**
 * Compare a latest score against an optional prior score. Used both
 * by `summarizeAssessmentTrend` and as a public helper for tests /
 * future callers that already have the two values in hand.
 * @agentic @benchmark/core
 */ const computeAssessmentDirection = (latest, prior)=>{
    if (prior === undefined) return "none";
    if (latest > prior) return "up";
    if (latest < prior) return "down";
    return "stable";
};
/**
 * Percent change of a latest score against its prior, rounded to the
 * nearest integer. Returns `undefined` when there is no prior or the
 * prior is `0` (percent is undefined / infinite) — the registry falls
 * back to the absolute point delta in that case. The sign is preserved
 * (negative = the score fell); callers decide how to color it.
 * @agentic @benchmark/core
 */ const computeAssessmentTrendPercent = (latest, prior)=>{
    if (prior === undefined || prior === 0) return undefined;
    return Math.round((latest - prior) / prior * 100);
};
/**
 * Summarize an unordered Observation list into a `{ value, date,
 * direction }` snapshot. Observations are filtered to the 6-week
 * freshness window before `monthEnd`, sorted newest-first, and the
 * top two drive the direction indicator. Returns `undefined` when
 * no fresh observation exists in the window.
 * @agentic @benchmark/core
 */ const summarizeAssessmentTrend = (observations, monthEnd)=>{
    const monthEndMs = Date.parse(monthEnd);
    if (!Number.isFinite(monthEndMs)) return undefined;
    const freshnessStartMs = monthEndMs - ASSESSMENT_FRESHNESS_MS;
    const fresh = [];
    for (const obs of observations){
        if (!isActiveStatus(obs.status)) continue;
        const effective = observationEffective(obs);
        if (!effective) continue;
        const timestampMs = Date.parse(effective);
        if (!Number.isFinite(timestampMs)) continue;
        if (timestampMs < freshnessStartMs || timestampMs > monthEndMs) continue;
        const value = observationValue(obs);
        if (value === undefined) continue;
        fresh.push({
            value,
            date: effective,
            timestampMs
        });
    }
    if (fresh.length === 0) return undefined;
    fresh.sort((a, b)=>b.timestampMs - a.timestampMs);
    const latest = fresh[0];
    const prior = fresh[1];
    return {
        value: latest.value,
        date: latest.date,
        direction: computeAssessmentDirection(latest.value, prior?.value),
        priorValue: prior?.value
    };
};

export { ASSESSMENT_FRESHNESS_MS, CARE_UNITS_BY_CODE, COCM_BILLING_CODE_LEGEND, COCM_BILLING_RULES_VERSION, INITIAL_WITH_ASSESSMENT, NO_ASSESSMENT, SUBSEQUENT_WITH_ASSESSMENT, buildBillingCodePacket, buildScoreInterpretation, calculateASRSScore, calculateCSSRSScore, calculateQuestionnaireScore, calculateWPAIScore, careUnitsForCodes, computeAssessmentDirection, computeAssessmentTrendPercent, getInterpretationFromScore, getLoincCodeForQuestionnaire, getQuestionnaireUrlFromLoincCode, getScoringRangeForTotal, resolveBillingCode, scoreAdministeredQuestionnaire, scoreByCanonicalQuestionnaireUrl, scoreLikertQuestionnaire, sumLikertScoresFromAnswerCodes, summarizeAssessmentTrend };
